/*
 * bc-stretch-chart
 *
 * Interactive webbing stretch-comparison line chart, drawn as bespoke SVG (no
 * charting library). Reads a JSON data island emitted by snippets/bc-stretch-
 * chart.liquid:
 *
 *   { "loads": [1..12],
 *     "series": [ { handle, title, url, legacy, color, points:[{kn,pct}] } ] }
 *
 * X axis = load (kN, 1–12), Y axis = stretch (%), one colored line per webbing
 * (colored by its real webbing.line_color). Features:
 *   - legend chips toggle individual series
 *   - a "Show discontinued (Legacy)" master toggle (off by default) reveals the
 *     legacy webbings as dashed, muted lines
 *   - highlight mode (data-highlight="<handle>") draws that webbing bright + thick
 *     and dims the rest — used on the product page
 *   - crosshair tooltip snapping to the nearest integer kN, listing every visible
 *     webbing's value at that load (keyboard accessible via arrow keys)
 *
 * The SVG is built lazily (IntersectionObserver) since the chart is below the
 * fold; the server-rendered <table> is the no-JS fallback.
 */
(() => {
  if (customElements.get('bc-stretch-chart')) return;

  const SVGNS = 'http://www.w3.org/2000/svg';
  const LEGACY_DASH = '5 4';

  // ---- small helpers --------------------------------------------------------

  function svgEl(tag, attrs) {
    const node = document.createElementNS(SVGNS, tag);
    if (attrs) {
      for (const k in attrs) {
        if (attrs[k] != null) node.setAttribute(k, attrs[k]);
      }
    }
    return node;
  }

  function htmlEl(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  // Deterministic fallback color from a handle, when a webbing has no line_color.
  function fallbackColor(handle) {
    let h = 0;
    for (let i = 0; i < handle.length; i++) {
      h = (h * 31 + handle.charCodeAt(i)) % 360;
    }
    return `hsl(${h}, 62%, 48%)`;
  }

  // "Nice" axis ceiling so the top gridline is a round number.
  function niceCeil(max) {
    if (max <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(max)));
    const n = max / pow;
    let step;
    if (n <= 1) step = 1;
    else if (n <= 2) step = 2;
    else if (n <= 2.5) step = 2.5;
    else if (n <= 5) step = 5;
    else step = 10;
    return step * pow;
  }

  class BcStretchChart extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      const dataNode = this.querySelector('script[type="application/json"][data-bc-stretch-data]');
      this.plot = this.querySelector('[data-bc-plot]');
      this.legendEl = this.querySelector('[data-bc-legend]');
      if (!dataNode || !this.plot) return;

      let data;
      try {
        data = JSON.parse(dataNode.textContent);
      } catch (e) {
        return;
      }
      this.loads = (data.loads && data.loads.length ? data.loads : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).slice();
      this.minLoad = this.loads[0];
      this.maxLoad = this.loads[this.loads.length - 1];

      this.highlight = (this.dataset.highlight || '').trim();
      this.legacyShown = this.dataset.showLegacy === 'true';
      this.activeLoad = null;

      // Build series state. Each carries a kn→pct lookup and a visibility flag
      // (individual chip toggle), independent of the legacy master toggle.
      this.series = (data.series || []).map((s) => {
        const byLoad = {};
        (s.points || []).forEach((p) => {
          if (p && p.kn != null && p.pct != null) byLoad[p.kn] = p.pct;
        });
        return {
          handle: s.handle,
          title: s.title || s.handle,
          legacy: !!s.legacy,
          color: s.color && s.color.length ? s.color : fallbackColor(s.handle || ''),
          byLoad,
          // PDP (highlight mode) opens showing only the chosen webbing; every
          // other line starts off and can be toggled on from the legend. The main
          // chart (no highlight) starts with everything visible.
          visible: this.highlight ? s.handle === this.highlight : true,
        };
      });
      if (!this.series.length) return;

      // Sortable main table — wired immediately (not gated on the lazy chart
      // build) so it works the moment the data-table disclosure is opened.
      this._initTableSort();

      this._build = this._build.bind(this);

      // Lazy-build when the chart nears the viewport.
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._io.disconnect();
            this._build();
          }
        }, { rootMargin: '300px 0px' });
        this._io.observe(this);
      } else {
        this._build();
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    // ---- sortable data table (multi mode only) ------------------------------

    _initTableSort() {
      const table = this.querySelector('.bc-stretch-chart__table--multi');
      if (!table || !table.tHead || !table.tBodies[0]) return;
      const headerRow = table.tHead.rows[0];
      if (!headerRow) return;
      const headers = Array.from(headerRow.cells);
      const tbody = table.tBodies[0];
      this._sortState = { col: -1, dir: 1 };

      // Map each row to its webbing handle so the chart selection can hide rows.
      this._rows = {};
      Array.from(tbody.rows).forEach((row) => {
        const h = row.getAttribute('data-handle');
        if (h) this._rows[h] = row;
      });

      headers.forEach((th, colIndex) => {
        const label = th.textContent.trim();
        th.textContent = '';
        th.setAttribute('aria-sort', 'none');

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bc-stretch-chart__sort-btn';
        const text = document.createElement('span');
        text.textContent = label;
        const arrow = document.createElement('span');
        arrow.className = 'bc-stretch-chart__sort-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        btn.appendChild(text);
        btn.appendChild(arrow);
        btn.addEventListener('click', () => this._sortBy(colIndex, headers, tbody, th));
        th.appendChild(btn);
      });

      // Initial sync: legacy rows start hidden, matching the chart's default.
      this._syncTable();
    }

    _sortBy(col, headers, tbody, th) {
      const isName = col === 0;
      // Same column again → flip direction; new column → name ascends, numbers
      // descend (largest stretch first, which is the more useful default).
      const dir = this._sortState.col === col ? -this._sortState.dir : (isName ? 1 : -1);
      this._sortState = { col, dir };

      const keyOf = (row) => {
        if (isName) return (row.getAttribute('data-name') || row.cells[0].textContent).trim().toLowerCase();
        const cell = row.cells[col];
        const num = cell ? parseFloat(cell.textContent.replace('%', '').trim()) : NaN;
        return isNaN(num) ? null : num;
      };

      Array.from(tbody.rows)
        .sort((a, b) => {
          const ka = keyOf(a);
          const kb = keyOf(b);
          if (ka === null && kb === null) return 0;
          if (ka === null) return 1; // missing (—) values always sink to the bottom
          if (kb === null) return -1;
          if (isName) return dir * String(ka).localeCompare(String(kb));
          return dir * (ka - kb);
        })
        .forEach((row) => tbody.appendChild(row));

      headers.forEach((h) => h.setAttribute('aria-sort', 'none'));
      th.setAttribute('aria-sort', dir === 1 ? 'ascending' : 'descending');
      this.querySelectorAll('.bc-stretch-chart__sort-btn').forEach((b) => b.classList.remove('is-asc', 'is-desc'));
      const activeBtn = th.querySelector('.bc-stretch-chart__sort-btn');
      if (activeBtn) activeBtn.classList.add(dir === 1 ? 'is-asc' : 'is-desc');
    }

    // ---- one-time scaffolding ----------------------------------------------

    _build() {
      if (this._built) return;
      this._built = true;

      const note = this.plot.querySelector('[data-bc-noscript]');
      if (note) note.remove();

      // SVG host
      this.svg = svgEl('svg', {
        class: 'bc-stretch-chart__svg',
        role: 'img',
        tabindex: '0',
        'aria-label': 'Webbing stretch comparison line chart. Force on the horizontal axis in kilonewtons, stretch on the vertical axis in percent. Press the arrow keys to read each webbing’s value at every force.',
      });
      this.plot.appendChild(this.svg);

      // Tooltip + live region (HTML overlays)
      this.tooltip = htmlEl('div', 'bc-stretch-chart__tooltip');
      this.tooltip.setAttribute('role', 'presentation');
      this.plot.appendChild(this.tooltip);

      this.live = htmlEl('div', 'visually-hidden');
      this.live.setAttribute('aria-live', 'polite');
      this.plot.appendChild(this.live);

      this._buildLegend();

      // Pointer + keyboard interaction
      this.svg.addEventListener('pointermove', (e) => this._onPointer(e));
      this.svg.addEventListener('pointerdown', (e) => this._onPointer(e));
      this.svg.addEventListener('pointerleave', () => this._clearActive());
      this.svg.addEventListener('keydown', (e) => this._onKey(e));
      this.svg.addEventListener('blur', () => this._clearActive());

      // Re-render geometry on width change. ResizeObserver on the (fluid-width)
      // plot already catches viewport resizes, so only fall back to a window
      // listener when RO is unavailable — and track it so it can be removed.
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this._scheduleRender());
        this._ro.observe(this.plot);
      } else {
        this._onResize = () => this._scheduleRender();
        window.addEventListener('resize', this._onResize);
      }

      this._render();
    }

    _scheduleRender() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._render();
      });
    }

    // ---- legend -------------------------------------------------------------

    _buildLegend() {
      if (!this.legendEl) return;
      this.chips = {};

      // "Select all / Deselect all" — first control in the selector, on both the
      // main chart and single-product (PDP) highlight mode. Label reflects the
      // action: "Deselect all" while anything is plotted, else "Select all".
      const all = htmlEl('button', 'bc-stretch-chart__chip bc-stretch-chart__select-all');
      all.type = 'button';
      all.addEventListener('click', () => {
        const turnOff = this._drawn().length > 0;
        this.series.forEach((s) => {
          s.visible = !turnOff;
          if (this.chips[s.handle]) this.chips[s.handle].setAttribute('aria-pressed', String(s.visible));
        });
        this._updateSelectAllLabel();
        this._syncTable();
        this._render();
      });
      this._selectAllBtn = all;
      this.legendEl.appendChild(all);
      this.legendEl.appendChild(htmlEl('span', 'bc-stretch-chart__legend-sep'));

      // Lead the legend with the chosen (highlighted) webbing on the PDP; the
      // rest keep their original order (Array.sort is stable). The main chart has
      // no highlight, so the order is left untouched there.
      const legendOrder = this.highlight
        ? this.series.slice().sort((a, b) =>
            (a.handle === this.highlight ? 0 : 1) - (b.handle === this.highlight ? 0 : 1))
        : this.series;
      legendOrder.forEach((s) => {
        const chip = htmlEl('button', 'bc-stretch-chart__chip');
        chip.type = 'button';
        chip.setAttribute('aria-pressed', String(s.visible));
        if (s.legacy) chip.classList.add('is-legacy');
        if (s.handle === this.highlight) chip.classList.add('is-highlight');

        const sw = htmlEl('span', 'bc-stretch-chart__chip-swatch');
        sw.style.background = s.color;
        const name = htmlEl('span', 'bc-stretch-chart__chip-name');
        name.textContent = s.title;
        chip.appendChild(sw);
        chip.appendChild(name);

        chip.addEventListener('click', () => {
          s.visible = !s.visible;
          chip.setAttribute('aria-pressed', String(s.visible));
          this._updateSelectAllLabel();
          this._syncTable();
          this._render();
        });

        // legacy chips are hidden until the master toggle is on — except the
        // highlighted webbing's own chip, which always shows (it's the subject).
        if (s.legacy && s.handle !== this.highlight) {
          chip.style.display = this.legacyShown ? '' : 'none';
        }

        this.legendEl.appendChild(chip);
        this.chips[s.handle] = chip;
      });

      // "Show discontinued (Legacy)" master toggle — only if there are legacy series.
      if (this.series.some((s) => s.legacy)) {
        const sep = htmlEl('span', 'bc-stretch-chart__legend-sep');
        this.legendEl.appendChild(sep);

        const toggle = htmlEl('button', 'bc-stretch-chart__chip bc-stretch-chart__legacy-toggle');
        toggle.type = 'button';
        toggle.setAttribute('aria-pressed', String(this.legacyShown));
        toggle.textContent = 'Show discontinued';
        toggle.addEventListener('click', () => {
          this.legacyShown = !this.legacyShown;
          toggle.setAttribute('aria-pressed', String(this.legacyShown));
          this.series.forEach((s) => {
            // never re-hide the highlighted webbing's own chip/line via this toggle
            if (!s.legacy || s.handle === this.highlight) return;
            if (this.legacyShown) s.visible = true; // reset so they reappear
            if (this.chips[s.handle]) {
              this.chips[s.handle].style.display = this.legacyShown ? '' : 'none';
              this.chips[s.handle].setAttribute('aria-pressed', String(s.visible));
            }
          });
          this._updateSelectAllLabel();
          this._syncTable();
          this._render();
        });
        this.legendEl.appendChild(toggle);
      }

      this._updateSelectAllLabel();
    }

    _updateSelectAllLabel() {
      if (!this._selectAllBtn) return;
      this._selectAllBtn.textContent = this._drawn().length > 0 ? 'Deselect all' : 'Select all';
    }

    // A series is drawn when its chip is on AND (it's not legacy, or legacy is
    // shown, or it's the highlighted webbing). The highlighted product is the
    // subject of the PDP chart, so it's never gated by the "Show discontinued"
    // toggle — even when it is itself a discontinued/Legacy webbing. The toggle
    // only governs the OTHER legacy comparison lines.
    _isDrawn(s) {
      return s.visible && (!s.legacy || this.legacyShown || s.handle === this.highlight);
    }

    _drawn() {
      return this.series.filter((s) => this._isDrawn(s));
    }

    // Match the data-table rows to the chart selection (one row per webbing).
    _syncTable() {
      if (!this._rows) return;
      this.series.forEach((s) => {
        const row = this._rows[s.handle];
        if (row) row.hidden = !this._isDrawn(s);
      });
    }

    // ---- render -------------------------------------------------------------

    _render() {
      const W = Math.round(this.plot.clientWidth || this.plot.getBoundingClientRect().width || 0);
      if (!W) return;
      const H = Math.max(260, Math.min(460, Math.round(W * 0.52)));
      this._W = W;

      const drawn = this._drawn();
      // Y ceiling from the visible series (min 1% so an empty chart still scales).
      let maxV = 0;
      drawn.forEach((s) => this.loads.forEach((l) => { if (s.byLoad[l] > maxV) maxV = s.byLoad[l]; }));
      const yMax = niceCeil(Math.max(maxV, 1));

      const ML = 48;
      const MR = 14;
      const MT = 16;
      const MB = 40;
      const innerW = Math.max(10, W - ML - MR);
      const innerH = Math.max(10, H - MT - MB);
      // Only the fields read back in _loadFromClientX / _showActive are stored.
      this._geom = { ML, MT, innerW, innerH };

      const x = (load) => ML + ((load - this.minLoad) / (this.maxLoad - this.minLoad)) * innerW;
      const y = (pct) => MT + innerH - (pct / yMax) * innerH;
      this._x = x;
      this._y = y;

      this.svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      while (this.svg.firstChild) this.svg.removeChild(this.svg.firstChild);

      // --- gridlines + Y ticks ---
      const yTicks = 5;
      for (let i = 0; i <= yTicks; i++) {
        const v = (yMax / yTicks) * i;
        const yy = y(v);
        this.svg.appendChild(svgEl('line', {
          class: 'bc-stretch-chart__gridline',
          x1: ML, y1: yy, x2: ML + innerW, y2: yy, 'stroke-width': 1,
        }));
        const lbl = svgEl('text', {
          class: 'bc-stretch-chart__axis-label',
          x: ML - 8, y: yy + 4, 'text-anchor': 'end', 'font-size': 11,
        });
        lbl.textContent = (Math.round(v * 10) / 10) + '%';
        this.svg.appendChild(lbl);
      }

      // --- X ticks (loads) ---
      const labelEvery = W < 460 ? 2 : 1;
      this.loads.forEach((l, i) => {
        const xx = x(l);
        this.svg.appendChild(svgEl('line', {
          class: 'bc-stretch-chart__gridline',
          x1: xx, y1: MT, x2: xx, y2: MT + innerH, 'stroke-width': 1, 'stroke-opacity': 0.5,
        }));
        const isEdge = i === 0 || i === this.loads.length - 1;
        if (isEdge || i % labelEvery === 0) {
          const lbl = svgEl('text', {
            class: 'bc-stretch-chart__axis-label',
            x: xx, y: MT + innerH + 18, 'text-anchor': 'middle', 'font-size': 11,
          });
          lbl.textContent = String(l);
          this.svg.appendChild(lbl);
        }
      });

      // axis titles
      const xTitle = svgEl('text', {
        class: 'bc-stretch-chart__axis-title',
        x: ML + innerW / 2, y: H - 4, 'text-anchor': 'middle',
        'font-size': 11, 'font-weight': 600,
      });
      xTitle.textContent = 'Force (kN)';
      this.svg.appendChild(xTitle);

      const yTitle = svgEl('text', {
        class: 'bc-stretch-chart__axis-title',
        x: 0, y: 0, 'text-anchor': 'middle',
        'font-size': 11, 'font-weight': 600,
        transform: `translate(12 ${MT + innerH / 2}) rotate(-90)`,
      });
      yTitle.textContent = 'Stretch (%)';
      this.svg.appendChild(yTitle);

      // --- series lines (highlight drawn last, on top) ---
      const hasHighlight = !!this.highlight && drawn.some((s) => s.handle === this.highlight);
      const ordered = drawn.slice().sort((a, b) => {
        const ah = a.handle === this.highlight ? 1 : 0;
        const bh = b.handle === this.highlight ? 1 : 0;
        return ah - bh;
      });

      ordered.forEach((s) => {
        const isHi = s.handle === this.highlight;
        const pts = [];
        this.loads.forEach((l) => { if (s.byLoad[l] != null) pts.push([x(l), y(s.byLoad[l])]); });
        if (pts.length < 1) return;

        let opacity = 0.9;
        let width = 2.2;
        if (hasHighlight) {
          opacity = isHi ? 1 : 0.4;
          width = isHi ? 3.4 : 1.7;
        }
        const path = svgEl('path', {
          d: 'M' + pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L'),
          fill: 'none',
          stroke: s.color,
          'stroke-width': width,
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
          'stroke-opacity': opacity,
          'stroke-dasharray': s.legacy ? LEGACY_DASH : null,
        });
        this.svg.appendChild(path);

        // markers on the highlighted line for legibility
        if (isHi) {
          pts.forEach((p) => {
            this.svg.appendChild(svgEl('circle', {
              cx: p[0], cy: p[1], r: 3, fill: s.color,
            }));
          });
        }
      });

      // --- crosshair group (populated on hover/keys) ---
      this.crosshair = svgEl('g', { class: 'bc-stretch-chart__crosshair', 'aria-hidden': 'true' });
      this.svg.appendChild(this.crosshair);

      // keep an existing active readout in sync after a resize/toggle
      if (this.activeLoad != null) this._showActive(this.activeLoad);
    }

    // ---- interaction --------------------------------------------------------

    _loadFromClientX(clientX) {
      const rect = this.svg.getBoundingClientRect();
      // viewBox units == px (viewBox width == client width), but guard scaling.
      const scale = this._W / rect.width || 1;
      const xv = (clientX - rect.left) * scale;
      const { ML, innerW } = this._geom;
      const frac = Math.min(1, Math.max(0, (xv - ML) / innerW));
      const idx = Math.round(frac * (this.loads.length - 1));
      return this.loads[Math.min(this.loads.length - 1, Math.max(0, idx))];
    }

    _onPointer(e) {
      if (!this._geom) return;
      // Load snaps to one of 12 integer kN; skip the rebuild while the pointer
      // stays within the same load band so pointermove stays cheap.
      const load = this._loadFromClientX(e.clientX);
      if (load === this.activeLoad) return;
      this._showActive(load);
    }

    _onKey(e) {
      if (!this.loads.length) return;
      let idx = this.activeLoad == null ? -1 : this.loads.indexOf(this.activeLoad);
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        idx = Math.min(this.loads.length - 1, idx + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        idx = idx <= 0 ? 0 : idx - 1;
      } else if (e.key === 'Home') {
        idx = 0;
      } else if (e.key === 'End') {
        idx = this.loads.length - 1;
      } else if (e.key === 'Escape') {
        this._clearActive();
        return;
      } else {
        return;
      }
      e.preventDefault();
      this._showActive(this.loads[idx]);
    }

    _clearActive() {
      this.activeLoad = null;
      if (this.crosshair) while (this.crosshair.firstChild) this.crosshair.removeChild(this.crosshair.firstChild);
      if (this.tooltip) this.tooltip.classList.remove('is-visible');
    }

    _showActive(load) {
      this.activeLoad = load;
      if (!this._geom || !this.crosshair) return;
      const { MT, innerH } = this._geom;
      const xx = this._x(load);

      while (this.crosshair.firstChild) this.crosshair.removeChild(this.crosshair.firstChild);
      this.crosshair.appendChild(svgEl('line', {
        class: 'bc-stretch-chart__crosshair-line',
        x1: xx, y1: MT, x2: xx, y2: MT + innerH, 'stroke-width': 1, 'stroke-dasharray': '3 3',
      }));

      const drawn = this._drawn().filter((s) => s.byLoad[load] != null);
      drawn.forEach((s) => {
        const isHi = s.handle === this.highlight;
        this.crosshair.appendChild(svgEl('circle', {
          class: 'bc-stretch-chart__crosshair-dot',
          cx: xx, cy: this._y(s.byLoad[load]), r: isHi ? 4 : 3,
          fill: s.color, 'stroke-width': 1.5,
        }));
      });

      this._updateTooltip(load, xx, drawn);
    }

    _updateTooltip(load, xx, drawn) {
      if (!this.tooltip) return;
      const rows = drawn.slice().sort((a, b) => b.byLoad[load] - a.byLoad[load]);

      this.tooltip.innerHTML = '';
      const head = htmlEl('p', 'bc-stretch-chart__tooltip-load');
      head.textContent = `${load} kN`;
      this.tooltip.appendChild(head);

      const list = htmlEl('ul', 'bc-stretch-chart__tooltip-list');
      let liveText = `At ${load} kilonewtons: `;
      rows.forEach((s) => {
        const li = htmlEl('li', 'bc-stretch-chart__tooltip-row');
        if (s.handle === this.highlight) li.classList.add('is-highlight');
        const sw = htmlEl('span', 'bc-stretch-chart__tooltip-swatch');
        sw.style.background = s.color;
        const nm = htmlEl('span', 'bc-stretch-chart__tooltip-name');
        nm.textContent = s.title;
        const vl = htmlEl('span', 'bc-stretch-chart__tooltip-val');
        const pct = Number(s.byLoad[load]).toFixed(2);
        vl.textContent = pct + '%';
        li.appendChild(sw);
        li.appendChild(nm);
        li.appendChild(vl);
        list.appendChild(li);
        liveText += `${s.title} ${pct}%. `;
      });
      this.tooltip.appendChild(list);
      this.tooltip.classList.add('is-visible');
      if (this.live) this.live.textContent = liveText;

      // position: center on the crosshair, clamp within the plot
      const tipW = this.tooltip.offsetWidth || 180;
      let left = xx - tipW / 2;
      left = Math.max(0, Math.min(this._W - tipW, left));
      this.tooltip.style.left = left + 'px';
      this.tooltip.style.top = '4px';
    }
  }

  customElements.define('bc-stretch-chart', BcStretchChart);
})();
